import React, { useState, useEffect, useRef, useCallback } from 'react';
import { countQuestions, createQuestion, fetchRelatedQuestions, fetchRecentQuestions, answerQuestion } from '../../utils/asktrip';
import { localKeyUserTokens, localKeyInstitutionTokens } from '../AskTrip';
import { Link } from 'react-router-dom';
import LoginMdl from '../../components/LoginMdl';
import SignupMdl from '../../components/SignupMdl';
import ErrorMdl from '../../components/errorMdl';

// Page to show a form for submitting a new question - https://www.tripdatabase.com/ask
export function AskTripForm() {

    const placeholderText = "e.g. What is the best treatment for hypertension?";

    // the question being asked
    const [originalQuestion, setOriginalQuestion] = useState('');

    // are we currently submitting the question
    const [submitting, setSubmitting] = useState(false);

    // if we are submitting, what message are we currently showing (e.g. "Hold on tight, we're thinking...")
    const [message, setMessage] = useState('');

    // if we have an error, what is it?
    const [error, setError] = useState(null);

    // check if user is logged in
    const [needsLogin, setNeedsLogin] = useState();

    // flag to show the login or signup modal
    const [showLoginOrSignup, setShowLoginOrSignup] = useState(false);

    // check if email is verified
    const [needsVerification, setNeedsVerification] = useState();

    // Modal state for login/signup
    const [loginshow, setLoginShow] = useState(false);
    const [signupshow, setSignupShow] = useState(false);
    const [hasError, setErrors] = useState(false);
    const [errorshow, setErrorShow] = useState(false);

    // New flag for whether user can ask questions (email verification)
    const [canCreateQuestion, setCanCreateQuestion] = useState(false);

    // How many questions the user has remaining this month (null=undetermined yet)
    const [questionsRemaining, setQuestionsRemaining] = useState(null);

    // the question limit modal
    const [showLimitModal, setShowLimitModal] = useState(false);

    // the user id, if any
    const [userId, setUserId] = useState(null);

    // the institution id, if any
    const [institutionId, setInstitutionId] = useState(null);

    const [tokens, setTokens] = useState(localStorage.getItem(localKeyUserTokens));

    const [institutionTokens, _setInstitutionTokens] = useState(localStorage.getItem(localKeyInstitutionTokens));

    const [similarQuestions, setSimilar] = useState(null);

    // effect that runs onload to establish if the user can create a question
    useEffect(() => {

        setCanCreateQuestion(false);
        setNeedsLogin(false);
        setNeedsVerification(false);
        setShowLimitModal(false);
        setQuestionsRemaining(null);
        setInstitutionId(null);
        setUserId(null);

        if (institutionTokens !== null && institutionTokens !== '') {

            try {
                const parsedInstitutionTokens = JSON.parse(institutionTokens);
                if (parsedInstitutionTokens.institution_auth) {
                    setCanCreateQuestion(true);
                    setInstitutionId(parsedInstitutionTokens.institution_auth.id);
                    return;
                }
            }
            catch (e) {
                // ignore and continue
            }

        }

        // if it's missing or empty, we need to login
        if (tokens === null || tokens === '') {
            setNeedsLogin(true);
            setNeedsVerification(false);
            return;
        } else {

            // attempt to parse the tokens, quietly ignoring any errors
            try {
                const parsedTokens = JSON.parse(JSON.parse(tokens));

                // if we have a tokens object, but no "id" property, we need to login
                if (!parsedTokens.id) {
                    setNeedsLogin(true);
                    return;
                }

                setNeedsLogin(false);
                setUserId(parsedTokens.id);

                // if we're logged in, but don't have a "emailVerified" property, we need to verify email
                if (!parsedTokens.emailVerified) {
                    setNeedsVerification(true);
                } else {

                    // if we're loggined and pro, there's no limit
                    if (parsedTokens.isPro == 1) {
                        setCanCreateQuestion(true);
                    } else {

                        // if we're logged in and not pro, we need to check how many questions they have remaining this month

                        const last30Cutoff = new Date(new Date().getTime() - (30 * 24 * 60 * 60 * 1000)).toISOString(); // 30 days ago

                        countQuestions({ userId: parsedTokens.id, after: last30Cutoff }) // count questions in the last 30 days
                        .then((count) => {
                            const maxQuestions = 3; 
                            const remaining = maxQuestions - count;
                            setQuestionsRemaining(remaining);

                            if (remaining <=0 ) {
                                setShowLimitModal(true);
                            } else {
                                setCanCreateQuestion(true);
                            }
                        })

                    }
                }
            } catch (e) {
                // ignore JSON parse errors
                setNeedsLogin(true);
                return;
            }
        }


    }, [tokens, institutionTokens]);

    const processQuestion = async function (payload) {

        const messages1 = [
            'Reviewing your question...',
        ];
        const messageInterval1 = setInterval(() => {
            const nextMessage = messages1.shift();
            if (nextMessage === undefined) {
                clearInterval(messageInterval1);
            } else {
                setMessage(nextMessage);
            }
            
        }, 2000);
        setMessage(messages1.shift());

        const analysis = await createQuestion(payload);

        clearInterval(messageInterval1);

        if (analysis.problematic) {
            let feedback = `Your question is problematic. Please rephrase it to avoid any sensitive or inappropriate content.`;
            if (analysis.alternative) {
                feedback += ` How about "${analysis.alternative}"`;
            }
            setError(feedback);
            return;
        }

        if (analysis.pii) {
            setError("The system has flagged possible patient-identifiable information. As a precaution, please rephrase your question and submit it again.");
            return;
        }

        if (!analysis.reasonable) {

            let feedback = `Your question is not reasonable. Please rephrase it to be more specific and clear.`;
            if (analysis.alternative) {
                feedback += ` How about "${analysis.alternative}"`;
            }
            setError(feedback);
            return;
        }

        setMessage("Looking for similar questions...");

        const similarQuestions = await fetchRelatedQuestions(analysis.questionId, true);

        if (similarQuestions.length > 0 && similarQuestions[0].similarity < -0.5) {
            setSimilar(similarQuestions);
            setMessage(null);
            await waitForSimilarQuestions();
        }

        const startTime = Date.now();

        const messages2 = [
            'Creating a smart search...',
            'Searching Trip for high-quality evidence...',
            'Identifying the most relevant information...',
            'Extracting key points...',
            'Drafting a clear and helpful answer...',
            'Doing a final check...',
            'The right answer is worth the wait...',
        ];
        const messageInterval2 = setInterval(() => {
            const nextMessage = messages2.shift();
            if (nextMessage === undefined) {
                clearInterval(messageInterval2);
            } else {
                setMessage(nextMessage);
            }
        }, 4500);
        setMessage(messages2.shift());

        await answerQuestion(analysis.questionId);

        clearInterval(messageInterval2);

        const endTime = Date.now();

        const elapsed = Math.round((endTime - startTime) / 1000);

        window.location.href = `/ask/${analysis.slug}-${analysis.questionId}`;

        return true; // return true to indicate we handled the question processing and a redirect is happening
    };

    const similarPause = useRef(null);

    async function waitForSimilarQuestions() {
        return new Promise((resolve, reject) => {
            similarPause.current = resolve;
        });
    }

    function continueQuestion() {
        if (similarPause.current) {
            similarPause.current();
            similarPause.current = null;
            setSimilar(null);
        }
    }

    // submitting the question, updating the message as it goes
    const submitQuestion = useCallback(() => {

        setSubmitting(true);
        setError(null);

        let redirecting = false;

        processQuestion({
            question: originalQuestion,
            userId: userId,
            institutionId: institutionId
        })
        .then((result) => {
            redirecting = result;

        })
            .catch((error) => {

                setError(`An error has occurred while submitting your question - please try again later. If the problem persists, please contact support.`);

            })
            .finally(() => {

                if (!redirecting) {
                    setSubmitting(false);
                    setMessage(null);
                }

            });

    }, [originalQuestion]);

    return <>


        <div className="ask-content container">
            {!submitting && (
                <div className="ask-form my-5 d-flex flex-column justify-content-center align-items-center position-relative">
                    <h2 className="huge-heading text-center">Ask a clinical question</h2>

                    {questionsRemaining !== null && (
                        <p className='alert alert-warning py-2'>
                            Please note you have <span className='bold'>{questionsRemaining < 0 ? 0 : questionsRemaining}</span> questions remaining this month.
                            <a className='ms-2' href="#" onClick={(e) => { e.preventDefault(); setShowLimitModal(true); }}>Why?</a>
                        </p>
                    )}

                    {error !== null && (
                        <p className='alert alert-danger py-2'>
                            {error}
                        </p>
                    )}

                    {!similarQuestions && <div className={`ask-form-wrapper position-relative ${!canCreateQuestion ? 'ask-form-disabled' : ''}`}>
                        <textarea
                            className="ask-form-textarea form-control ask-border"
                            placeholder={placeholderText}
                            rows="4"
                            value={originalQuestion}
                            onChange={e => setOriginalQuestion(e.target.value)}
                            onClick={e => { if (!canCreateQuestion) { setShowLoginOrSignup(true); e.preventDefault(); } }}
                        ></textarea>

                        <button
                            className="btn btn-primary mt-3"
                            disabled={originalQuestion.length === 0 || !canCreateQuestion}
                            onClick={() => submitQuestion()}
                        >
                            Ask Question
                        </button>

                        {showLoginOrSignup && (
                            <div className="ask-verify-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center mt-4">
                                <div className="ask-verify-message bg-white rounded-3 shadow-lg p-4 text-center">
                                    <h3 className="h5 mb-2">Log in to ask </h3>
                                    <p className="text-muted mb-3">Please log in to submit a question to AskTrip.</p>
                                    <p className="text-muted mb-3"> You can browse previous questions without logging in.</p>
                                    <div className="d-flex gap-2 justify-content-center">
                                        <Link
                                            className="nav-link btn btn-primary"
                                            to="/Login"
                                            title="Login"
                                            onClick={(e) => {
                                                setErrors("");
                                                setLoginShow(!loginshow);
                                                setSignupShow(false);
                                                e.preventDefault();
                                            }}
                                        >
                                            Login
                                        </Link>
                                        <Link
                                            className="nav-link btn"
                                            to="/Registration"
                                            title="Sign up"
                                            onClick={(e) => {
                                                setErrors("");
                                                setSignupShow(!signupshow);
                                                setLoginShow(false);
                                                e.preventDefault();
                                            }}
                                        >
                                            Sign Up
                                        </Link>
                                         <a className="nav-link btn"
                                            onClick={(e) => {
                                                setErrors("");
                                                setShowLoginOrSignup(false);
                                                e.preventDefault();
                                            }}
                                        >
                                            Dismiss
                                        </a>

                                        { window.location.hostname === 'localhost' && <button onClick={ () => setTokens(JSON.stringify( JSON.stringify( { id:999, isPro:1, emailVerified:true } ))) } >Fake</button> }
                                    </div>
                                </div>
                            </div>
                        )}

                        {needsVerification && (
                            <div className="ask-verify-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                                <div className="ask-verify-message bg-white rounded-3 shadow-lg p-4 text-center">
                                    <h3 className="h5 mb-2">Verify your email to continue</h3>
                                    <p className="text-muted mb-3">Please verify your email address before submitting questions to AskTrip.</p>
                                    <a onClick={() => setNeedsVerification(false)} className="btn btn-primary px-3">Verify Email</a>
                                </div>
                            </div>
                        )}
                    </div>}
                </div>
            )}



            {similarQuestions &&

                <>
                    <div className='my-5 ask-similar-question-prompt'>
                        <h3 className="my-2 mb-5 text-center h5">Similar questions</h3>
                        <p>You asked “<i className=''>{originalQuestion}</i>”</p>
                        <p>
                            We found some similar questions that might already answer your query:
                        </p>

                        <ul className="list-unstyled d-flex  justify-content-center flex-wrap gap-2">
                            {similarQuestions.map((q, i) => (
                                <li key={q.questionId} className="text-decoration-none px-3 py-2 bg-primary d-inline-block rounded-pill">
                                    <a href={`/ask/${q.slug}-${q.questionId}`} className='text-decoration-none text-white'>{q.question}</a> 
                                </li>
                            ))}


                        </ul>
                        <p><button
                            className="btn btn-outline-primary mt-3"
                            onClick={() => continueQuestion()}
                            >
                            Continue with my original question...
                        </button></p>
                    </div>
                </>}

            {submitting && (
                <div className="ask-submitting d-flex flex-column justify-content-center align-items-center position-relative">
                    <div className="ask-message-wrapper">
                        <div key={message} className="ask-message-animated fade-in-up">
                            {message}
                        </div>
                    </div>
                </div>
            )}



            {!submitting && <RecentQuestions />}
        </div>
        
        {/* Login/Signup Modals */}
        <LoginMdl
            show={loginshow}
            onHide={() => setLoginShow(false)}
            setLoginShow={setLoginShow}
            setSignupShow={setSignupShow}
            setErrors={setErrors}
            setErrorShow={setErrorShow}
            want_url={window.location.pathname}
        />
        <SignupMdl
            show={signupshow}
            onHide={() => setSignupShow(false)}
            setLoginShow={setLoginShow}
            setSignupShow={setSignupShow}
            setErrors={setErrors}
            setErrorShow={setErrorShow}
        />
        <ErrorMdl
            show={errorshow}
            errors={hasError}
            onHide={() => setErrorShow(false)}
        />

        {/* Question Limit Modal */}
        {showLimitModal && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Question Limits Explained</h5>
                            <button type="button" className="btn-close" onClick={() => setShowLimitModal(false)}></button>
                        </div>
                        <div className="modal-body">
                            <p>AskTrip uses advanced AI technology to provide high-quality clinical answers. To ensure sustainable service for all users, we have monthly question limits based on your subscription tier.</p>

                            <h6 className="mt-3">Subscription Tiers:</h6>
                            <ul>
                                <li><strong>Free Users:</strong> 3 questions per month</li>
                                <li><strong>Pro Users:</strong> Unlimited questions</li>
                                <li><strong>Enterprise Users:</strong> Unlimited questions</li>
                            </ul>

                            <p className="mt-3">Your question limit resets on the first day of each month. Questions are counted when you submit them, regardless of whether you view the answer.</p>

                            <p className="mb-0">Need more questions? <a href="/upgrade">Upgrade your subscription</a> to increase your monthly limit.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowLimitModal(false)}>Close</button>
                            <button type="button" className="btn btn-primary" onClick={() => window.location.href = '/upgrade'}>Upgrade Now</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>

}

function RecentQuestions() {

    const [questions, setQuestions] = useState(null);

    // Fetch recent questions from the backend
    useEffect(() => {

        fetchRecentQuestions( { limit:10 } ).then((data) => {
            setQuestions(data);
        });

    }, []);

    return <>
        {questions && <div className='ask-recent-questions my-5'>
            <h3 className='mb-3 text-center h5'>Recent questions</h3>
            <ul className='list-unstyled d-flex justify-content-center flex-wrap gap-2'>
                {questions.map((q, i) => (
                    <li key={q.questionId}>
                        <a href={`/ask/${q.slug}-${q.questionId}`} className='text-decoration-none px-3 py-2 bg-white d-inline-block rounded-pill text-primary'>{q.question}</a>
                    </li>
                ))}
            </ul>
            <div className='text-center py-3'><a className='btn' href="/ask/archive">More questions...</a></div>
        </div>}
    </>;

}
